import { motion } from 'framer-motion';
import { Upload, Settings, Printer, Truck, CheckCircle } from 'lucide-react';

const steps = [
  {
    icon: Upload,
    title: 'Escolha ou Envie',
    description: 'Selecione um produto do nosso catálogo ou envie seu próprio modelo 3D para impressão personalizada.',
    color: 'from-orange-500 to-red-500',
  },
  {
    icon: Settings,
    title: 'Personalize',
    description: 'Escolha o material, cor e acabamento. Nossa equipe revisa todos os detalhes do seu projeto.',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    icon: Printer,
    title: 'Impressão 3D',
    description: 'Utilizamos impressoras de alta precisão para garantir qualidade excepcional em cada detalhe.',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: Truck,
    title: 'Entrega Rápida',
    description: 'Embalamos com cuidado e enviamos para todo o Brasil. Acompanhe seu pedido em tempo real.',
    color: 'from-purple-500 to-pink-500',
  },
];

export function HowItWorksSection() {
  return (
    <section className="py-24 bg-gray-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-orange-500/5 rounded-full blur-[100px] -translate-y-1/2" />
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-cyan-500/5 rounded-full blur-[100px] -translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 text-orange-400 text-sm font-medium mb-4">
            Processo Simples
          </span>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Como{' '}
            <span className="bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent">
              Funciona
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Do conceito à realidade em poucos passos. Transformamos suas ideias em objetos tangíveis 
            com tecnologia de ponta.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500/30 via-cyan-500/30 to-purple-500/30 -translate-y-1/2" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                className="relative"
              >
                <div className="text-center">
                  {/* Icon */}
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                    className={`relative w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${step.color} p-[2px]`}
                  >
                    <div className="w-full h-full rounded-2xl bg-gray-900 flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    {/* Step Number */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white text-gray-900 font-bold flex items-center justify-center text-sm shadow-lg">
                      {index + 1}
                    </div>
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>

                {/* Checkmark for completed feel */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:flex absolute top-10 -right-4 transform translate-x-1/2 z-10">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 + 0.3, duration: 0.3 }}
                    >
                      <CheckCircle className="w-6 h-6 text-green-500 bg-gray-900 rounded-full" />
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { value: '99%', label: 'Taxa de Satisfação' },
            { value: '48h', label: 'Tempo Médio de Produção' },
            { value: '+50k', label: 'Peças Impressas' },
            { value: '100%', label: 'Qualidade Garantida' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 + index * 0.1, duration: 0.5 }}
              className="text-center p-6 rounded-2xl bg-gray-800/50 border border-white/5"
            >
              <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-400 to-cyan-400 bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-gray-400 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
